import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { MdModeEdit } from "react-icons/md";
import { FaArrowRight } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import * as apicall from "../../../components/Apicall";
import Loader from "../../../components/Loader/Loader";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import { toast } from "react-toastify";
import { toTitleCase } from "../../../utils/textUtils";

const thStyle = {
  background: "#F9FAFB",
  fontWeight: 600,
  fontSize: "13px",
  color: "#374151",
  padding: "12px 16px",
  borderBottom: "1px solid #E5E7EB",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px 16px",
  fontSize: "14px",
  color: "#374151",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "middle",
};

function ProviderRow({ item, index, onToggle, onRefresh }) {
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState(item.name);
  const [commission, setCommission] = useState(
    item.commission !== null && item.commission !== undefined
      ? String(item.commission)
      : ""
  );
  const [saving, setSaving] = useState(false);

  const handleEdit = async () => {
    try {
      setSaving(true);
      await apicall.post({ id: item.id, name, commission }, "editPartner");
      setEditMode(false);
      onRefresh();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr
      onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      style={{ transition: "background 0.15s" }}
    >
      <td style={tdStyle}>{index + 1}</td>
      <td style={tdStyle}>
        {editMode ? (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleEdit()}
            className="form-control"
            style={{ maxWidth: "220px" }}
            autoFocus
          />
        ) : (
          toTitleCase(item.name)
        )}
      </td>
      <td style={tdStyle}>
        {editMode ? (
          <input
            type="text"
            value={commission}
            onChange={(e) =>
              setCommission(e.target.value.replace(/[^0-9]/g, ""))
            }
            className="form-control"
            style={{ maxWidth: "100px" }}
            placeholder="%"
          />
        ) : item.commission ? (
          `${item.commission}%`
        ) : (
          "-"
        )}
      </td>
      <td style={{ ...tdStyle, cursor: "pointer", color: "#C0335A" }}>
        {editMode ? (
          saving ? (
            <Loader style={{ width: 30, height: 16 }} />
          ) : (
            <FaArrowRight onClick={handleEdit} style={{ cursor: "pointer" }} />
          )
        ) : (
          <MdModeEdit size={17} onClick={() => setEditMode(true)} />
        )}
      </td>
      <td style={tdStyle}>
        <Form.Check
          type="switch"
          checked={item.is_active === 1}
          onChange={() => onToggle(item)}
          id={`provider-switch-${item.id}`}
        />
      </td>
    </tr>
  );
}

function ProvidersTab({ onComplete }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addName, setAddName] = useState("");
  const [addCommission, setAddCommission] = useState("");
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);

  const [showInactivePanel, setShowInactivePanel] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [confirmingInactive, setConfirmingInactive] = useState(false);

  useEffect(() => {
    fetchProviders();
  }, []);

  async function fetchProviders() {
    try {
      setLoading(true);
      const res = await apicall.get("partners");
      if (res.success && res.data) {
        setProviders(
          res.data.map((p) => ({
            id: p.id,
            name: p.partner_name,
            commission: p.commission,
            is_active: p.is_active,
          }))
        );
      } else {
        setProviders([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!addName.trim()) { setAddError("Provider name is required"); return; }
    try {
      setAdding(true);
      await apicall.post(
        { profile_name: addName, commission: addCommission || null },
        "add_profile"
      );
      resetAddPanel();
      fetchProviders();
      onComplete?.();
    } catch (e) {
      console.error(e);
    } finally {
      setAdding(false);
    }
  }

  const handleToggle = async (item) => {
    const newStatus = item.is_active === 1 ? 0 : 1;

    if (newStatus === 0) {
      try {
        const checkRes = await apicall.post(
          { id: item.id, type: "partner" },
          "check_active_status"
        );
        if (checkRes.hasBookings) {
          setPendingToggle({ item, count: checkRes.count });
          setShowInactivePanel(true);
          return;
        }
      } catch (e) {
        console.error(e);
      }
    }

    try {
      await apicall.post(
        { id: item.id, is_active: newStatus, type: "partner" },
        "active_status"
      );
      fetchProviders();
    } catch (e) {
      console.error(e);
    }
  };

  const confirmInactive = async () => {
    if (!pendingToggle) return;
    try {
      setConfirmingInactive(true);
      await apicall.post(
        { id: pendingToggle.item.id, is_active: 0, type: "partner" },
        "active_status"
      );
      fetchProviders();
    } catch (e) {
      console.error(e);
    } finally {
      setConfirmingInactive(false);
      setShowInactivePanel(false);
      setPendingToggle(null);
    }
  };

  const resetAddPanel = () => {
    setShowAddPanel(false);
    setAddError("");
    setAddName("");
    setAddCommission("");
  };

  const filtered = providers.filter((p) =>
    p.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Row 1: Title + action */}
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1 fw-bold" style={{ color: "#111827" }}>
            Providers
          </h5>
          <p className="mb-0" style={{ fontSize: "13px", color: "#6B7280" }}>
            Service partners who fulfill bookings. Manage commission per partner.
          </p>
        </div>
        <button
          onClick={() => setShowAddPanel(true)}
          className="btn btn-danger"
          style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600, height: "38px" }}
        >
          + New Provider
        </button>
      </div>

      {/* Row 2: Search */}
      <div className="mb-3">
        <div style={{ position: "relative", maxWidth: "320px" }}>
          <IoSearchOutline
            size={15}
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9CA3AF",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            className="form-control"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search providers..."
            style={{ paddingLeft: "32px", height: "38px", fontSize: "13px", borderRadius: "8px" }}
          />
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Sl. No", "Provider Name", "Commission", "Edit", "Active"].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5).fill(null).map((_, i) => (
                  <tr key={i}>
                    <td style={tdStyle}><Skeleton width="30px" /></td>
                    <td style={tdStyle}><Skeleton width="180px" /></td>
                    <td style={tdStyle}><Skeleton width="60px" /></td>
                    <td style={tdStyle}><Skeleton width="24px" /></td>
                    <td style={tdStyle}><Skeleton width="50px" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: "#6B7280", padding: "48px" }}>
                    {search ? `No results for "${search}"` : "No providers found"}
                  </td>
                </tr>
              ) : (
                filtered.map((item, idx) => (
                  <ProviderRow
                    key={item.id}
                    item={item}
                    index={idx}
                    onToggle={handleToggle}
                    onRefresh={fetchProviders}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Provider SlidePanel */}
      <SlidePanel
        show={showAddPanel}
        onHide={resetAddPanel}
        title="New Provider"
        footer={
          <>
            <button className="btn btn-outline-secondary px-4" onClick={resetAddPanel}>
              Cancel
            </button>
            <button
              className="btn Button primaryBackground text-white px-4"
              onClick={handleAdd}
              disabled={adding}
            >
              {adding ? <Loader style={{ width: 40, height: 20 }} /> : "Submit"}
            </button>
          </>
        }
      >
        <div
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        >
          <div className="mb-3">
            <label className="form-label" style={{ fontSize: "13px", fontWeight: 500 }}>
              Provider Name
            </label>
            <input
              type="text"
              className="form-control"
              value={addName}
              onChange={(e) => { setAddName(e.target.value); setAddError(""); }}
              placeholder="Enter provider name"
            />
            {addError && <small className="text-danger">{addError}</small>}
          </div>
           
          <div className="mb-3">
            <label className="form-label" style={{ fontSize: "13px", fontWeight: 500 }}>
              Commission %
            </label>
            <input
              type="text"
              className="form-control"
              value={addCommission}
              onChange={(e) =>
                setAddCommission(e.target.value.replace(/[^0-9]/g, ""))
              }
              placeholder="Optional"
            />
          </div>
         
        </div>
      </SlidePanel>

      {/* Inactive Confirmation SlidePanel */}
      <SlidePanel
        show={showInactivePanel}
        onHide={() => { setShowInactivePanel(false); setPendingToggle(null); }}
        title="Inactive Provider"
      >
        <p style={{ fontSize: "15px", color: "#374151" }}>
          There are <strong>{pendingToggle?.count}</strong> booking record(s) found for{" "}
          <strong>{pendingToggle?.item?.name}</strong>. If you inactive this provider,
          those bookings will not be editable.
        </p>
        <div className="d-flex gap-3 mt-4">
          <button
            onClick={() => { setShowInactivePanel(false); setPendingToggle(null); }}
            className="btn btn-outline-secondary px-4"
          >
            Cancel
          </button>
          <button
            onClick={confirmInactive}
            className="btn Button primaryBackground text-white px-4"
            disabled={confirmingInactive}
          >
            {confirmingInactive ? <Loader style={{ width: 40, height: 20 }} /> : "Inactive"}
          </button>
        </div>
      </SlidePanel>
    </div>
  );
}

export default ProvidersTab;
